import { useCallback } from 'react'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import {
  useListingWizardStore,
  WIZARD_STEPS,
  type WizardStep,
} from '../stores/listing-wizard.store'
import { listingStepDestination } from '../utils/listing-step-navigation'
import { wizardHrefForStep } from './use-open-listing-wizard'

/** Call only after the section's existing validation succeeds. No save logic lives here. */
export function useListingStepNavigation(nextStep: WizardStep) {
  const router = useRouter()
  const { t } = useTranslation()
  const returnsToReview = useListingWizardStore(
    (s) => s.entryMode === 'edit' || s.editReturnTo !== null,
  )
  const continueEditing = useCallback(() => {
    const draft = useListingWizardStore.getState()
    const destination = listingStepDestination(nextStep, draft.entryMode, draft.editReturnTo)
    const stepIndex = WIZARD_STEPS.indexOf(destination)
    draft.setEditReturnTo(null)
    draft.goToStep(stepIndex)
    // Only EDIT entries carry the id. A partially saved CREATE must not reload
    // its incomplete server row over the locally persisted draft.
    const href = wizardHrefForStep(stepIndex, draft.entryMode === 'edit' ? draft.vehicleId : null)
    if (returnsToReview) router.dismissTo(href as never)
    else router.push(href as never)
  }, [nextStep, returnsToReview, router])

  return {
    continueEditing,
    continueLabel: t(returnsToReview ? 'hosting.review_changes' : 'listing.next'),
    returnsToReview,
  }
}
